import React, { useState, useEffect, useCallback } from 'react';
import Head from 'next/head';
import Script from 'next/script';
import { useRouter } from 'next/router';
import { useSelector, useDispatch } from 'react-redux';
import Cookie from 'cookie';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import { apiClient } from '@/http-client';
import { formatPostData } from '@services/postService';
import { getLanguageId } from '@helpers/language';
import { Layout, Container, Button, StepProgress, Spinner } from '@components';
import { PageContext } from '@contexts';
import { reqLocationsAction } from '@stores/locations/actions';
import BuildAppNavigation from './BuildAppNavigation';
import DealerHeader from './DealerHeader';
import PageTitle from './PageTitle';
import PostalCodeModal from './PostalCodeModal';
import styles from './BuildQuote.module.scss';

export default function BuildSpecific(pageProps) {
  const dispatch = useDispatch();
  const router = useRouter();
  const { locale } = router;
  const { id: productID } = router.query;
  const langId = getLanguageId(locale);
  const { data } = pageProps;

  const [productList, setProductList] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasBQA, setHasBQA] = useState(false);

  const [showPostalModal, setShowPostalModal] = useState(false);

  useEffect(() => {
    async function loadBuildAppNavigation(productID) {
      const { data } = await apiClient.post(`/products/api/Products/buildAppNavigation/${productID}/-1?languageId=${langId}`);
      setProductList(data);
      setIsLoaded(true);
    }

    loadBuildAppNavigation(productID);
  }, [productID]);

  useEffect(() => {
    if (!productList?.productList) {
      return;
    }

    const product = productList.productList[0];
    if (!product) {
      router.replace('/404');
      return;
    }

    const res = product.Tags?.reduce((val, tag) => {
      return val || (tag.TagCode === 'BuildQuote' && tag.Value === 'BQA');
    }, false);
    setHasBQA(res);

    const cookies = Cookie.parse(document.cookie || '');
    if (!cookies.postalCode) {
      openModal();
    }
  }, [productList]);

  useEffect(() => {
    if (hasBQA && productID) {
      const route = `/buildandquote/customize/${productID}`;
      window.location.replace(locale === 'fr' ? `/fr${route}` : `${route}`);
      // router.replace(`/buildandquote/customize/${productID}`);
    }
  }, [hasBQA, productID]);

  const openModal = () => {
    setShowPostalModal(true);
  };

  const getLngLat = () => {
    const cookies = Cookie.parse(document.cookie || '');
    const lngLat = cookies?.lngLat ? cookies.lngLat : '';
    return lngLat;
  };

  const postalCodeUpdatedFromModal = (updatedValues) => {
    const lngLat = getLngLat();
    dispatch(reqLocationsAction({ latlng: `${lngLat}` }));
  };

  const navToProductID = useCallback((productID) => {
    router.push('/buildandquote/' + productID, undefined, { shallow: true });
  }, []);

  return (
    <PageContext.Provider value={data}>
      <Layout>
        <Container className={styles.buildQuote}>
          <Head>
            <title>{locale == 'fr' ? 'Construire et obtenir un devis' : 'Build & Quote'} | Kubota Canada</title>
            <meta
              property="og:title"
              content={`${locale == 'fr' ? 'Construire et obtenir un devis' : 'Build & Quote'} | Kubota Canada`}
            />

            <script src="/scripts/jquery-3.5.1.min.js" />
          </Head>

          <DealerHeader />

          <StepProgress
            steps={5}
            focus={1}
            texts={
              locale == 'fr' ?
                ['Sélectionner un produit', 'Choix du modèle', 'Compléments Kubota', 'Compléments Land Pride', 'Sommaire'] :
                ['Select a Product', 'Model Choice', 'Kubota Add-Ons', 'Land Pride Add-Ons', 'Summary']
            }
          />

          <PageTitle subtitle={locale == 'fr' ? 'Sélectionner un modèle' : 'Select a Model'} />

          {!productList && <div>Loading</div>}
          {!hasBQA && productList && (
            <BuildAppNavigation
              productList={productList}
              selectClicked={navToProductID}
              currentProductID={productID}
            />
          )}

          <PostalCodeModal
            showPostalModal={showPostalModal}
            setShowPostalModal={setShowPostalModal}
            updatePostalCode={postalCodeUpdatedFromModal}
           />
        </Container>
      </Layout>
    </PageContext.Provider>
  );
}

export const getServerSideProps = async ({ params, locale }) => {
  const { id } = params;
  const { data: post } = await apiClient.get('/products/api/Posts/1000996');

  const data = formatPostData(post);

  const checkTheme = data?.DisplayOptions
    ?.find((x) => x.Name == 'Header')
    ?.DisplayOptions
    ?.find((item) => item.DisplayOptionKey == 'DarkMode')
    ?.Enabled;
  data.DarkMode = checkTheme ? checkTheme : null;

  if (!data) {
    return {
      notFound: true,
      redirect: {
        permanent: true,
        destination: '/404'
      }
    };
  }

  const translations = await serverSideTranslations(locale, ['common', 'footer']);

  return {
    props: {
      data,
      ...translations
    }
  };
};
